// src/pages/About.jsx
import React, { useEffect, useMemo, } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";

// New libs
import Tilt from "react-parallax-tilt";
import { Particles } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim"; // lightweight engine
import AOS from "aos";
import "aos/dist/aos.css";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Assets (already in your repo)
import aboutBg from "../assets/about-bg.jpg";
import founderImg from "../assets/founder.jpg";
import heroImg from "../assets/hero.jpg";
import logo from "../assets/logo.png";
import img1 from "../assets/img1.jpg";
import idea from "../assets/idea.jpg";
import workshop from "../assets/workshop.jpg";

/* =========================
   Motion + UI Helpers
========================= */
const useFadeUp = (delay = 0) => {
  const reduce = useReducedMotion();
  return reduce
    ? { initial: false, whileInView: { opacity: 1 }, viewport: { once: true } }
    : {
        initial: { opacity: 0, y: 18 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-100px" },
        transition: { duration: 0.6, delay },
      };
};

const Card = ({ className = "", children, tilt = true }) => {
  const content = (
    <div
      className={
        "rounded-3xl border border-white/30 bg-white/60 backdrop-blur-md shadow-[0_8px_30px_rgb(0,0,0,0.07)] " +
        className
      }
    >
      {children}
    </div>
  );
  return tilt ? (
    <Tilt
      tiltMaxAngleX={6}
      tiltMaxAngleY={6}
      glareEnable
      glareMaxOpacity={0.12}
      glareColor="#ffffff"
      glarePosition="all"
      className="will-change-transform"
    >
      {content}
    </Tilt>
  ) : (
    content
  );
};

const Section = ({ id, title, intro, children }) => {
  const fade = useFadeUp(0);
  return (
    <section id={id} className="scroll-mt-24 mb-20">
      <motion.div {...fade} className="mb-6">
        <h2 className="text-2xl md:text-3xl font-extrabold text-blue-900">{title}</h2>
        {intro && <p className="text-gray-700 mt-2 max-w-3xl">{intro}</p>}
      </motion.div>
      <div className="text-gray-800 leading-relaxed">{children}</div>
    </section>
  );
};

const Stat = ({ k, v, sub, i = 0 }) => (
  <motion.div {...useFadeUp(0.03 * i)}>
    <Card className="p-6">
      <div className="text-3xl md:text-4xl font-extrabold text-blue-700">{k}</div>
      <div className="text-gray-800 font-semibold">{v}</div>
      {sub && <div className="text-gray-500 text-sm mt-1">{sub}</div>}
    </Card>
  </motion.div>
);

const ValueCard = ({ emoji, title, text, i = 0 }) => (
  <motion.div {...useFadeUp(0.02 * i)}>
    <Card className="p-6 hover:shadow-lg transition">
      <div className="flex items-center gap-2">
        <span className="text-2xl" aria-hidden="true">
          {emoji}
        </span>
        <h3 className="font-semibold text-blue-800">{title}</h3>
      </div>
      <p className="text-gray-700 mt-2">{text}</p>
    </Card>
  </motion.div>
);

const TimelineItem = ({ year, title, text, img, i = 0 }) => (
  <motion.div {...useFadeUp(0.03 * i)} className="relative pl-6 pb-12">
    <div className="absolute left-0 top-2 w-3 h-3 bg-blue-600 rounded-full" />
    <div className="grid md:grid-cols-[1fr_240px] gap-4 items-start">
      <div>
        <div className="text-sm text-gray-500">{year}</div>
        <div className="font-semibold text-blue-800">{title}</div>
        <div className="text-gray-700">{text}</div>
      </div>
      {img && (
        <img
          src={img}
          alt={title}
          className="rounded-xl w-full h-36 object-cover border"
          loading="lazy"
        />
      )}
    </div>
  </motion.div>
);

/* =========================
   Background FX (global)
========================= */
const BackgroundFX = () => {
  const reduce = useReducedMotion();

  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      {/* Slow moving gradient wash */}
      {!reduce && (
        <motion.div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(35% 55% at 15% 20%, rgba(59,130,246,0.18), transparent 60%), radial-gradient(45% 65% at 85% 25%, rgba(16,185,129,0.16), transparent 60%), radial-gradient(40% 60% at 50% 85%, rgba(139,92,246,0.14), transparent 60%)",
            backgroundSize: "200% 200%",
          }}
          initial={{ backgroundPosition: "0% 0%", opacity: 0.9 }}
          animate={{ backgroundPosition: ["0% 0%", "100% 50%", "50% 100%", "0% 0%"] }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
        />
      )}

      {/* Soft highlight orbs (parallax-ish) */}
      {!reduce && (
        <>
          <motion.div
            aria-hidden="true"
            className="absolute -top-24 -left-24 h-[28rem] w-[28rem] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(59,130,246,0.18), transparent 60%)" }}
            animate={{ x: [0, 20, -10, 0], y: [0, -10, 10, 0] }}
            transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            aria-hidden="true"
            className="absolute -bottom-24 -right-24 h-[28rem] w-[28rem] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(16,185,129,0.16), transparent 60%)" }}
            animate={{ x: [0, -20, 10, 0], y: [0, 12, -8, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          />
        </>
      )}
    </div>
  );
};

/* =========================
   Testimonials (react-slick)
========================= */
const Testimonials = () => {
  const items = [
    {
      quote:
        "Glide took the stress out of my application. Clear steps, quick feedback, and real care.",
      name: "International Student, Adelaide",
    },
    {
      quote:
        "They translated visa rules into plain English and kept me on track — highly recommend.",
      name: "Postgraduate Applicant",
    },
    {
      quote:
        "Super professional and transparent. Their OSHC and funds guidance saved me time and money.",
      name: "Masters Student",
    },
  ];

  const settings = {
    dots: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 4500,
    pauseOnHover: true,
    infinite: true,
    speed: 500,
  };

  return (
    <Card className="p-6" tilt={false}>
      <h3 className="text-xl font-bold text-blue-900 mb-4">What Students Say</h3>
      <Slider {...settings}>
        {items.map((t, idx) => (
          <div key={idx}>
            <blockquote className="text-gray-800 italic min-h-[120px] flex items-center">
              “{t.quote}”
            </blockquote>
            <div className="not-italic text-sm text-gray-500 mt-2">— {t.name}</div>
          </div>
        ))}
      </Slider>
    </Card>
  );
};

/* =========================
   CEO Spotlight (animated)
========================= */
const CEOSpotlight = () => {
  const reduce = useReducedMotion();

  return (
    <Card className="relative overflow-hidden" tilt={false}>
      {/* Animated gradient layer */}
      {!reduce && (
        <motion.div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(40% 60% at 20% 20%, rgba(59,130,246,0.22), transparent 60%), radial-gradient(35% 55% at 80% 30%, rgba(16,185,129,0.2), transparent 60%), radial-gradient(40% 60% at 50% 80%, rgba(139,92,246,0.18), transparent 60%)",
            backgroundSize: "200% 200%",
          }}
          initial={{ backgroundPosition: "0% 0%" }}
          animate={{ backgroundPosition: ["0% 0%", "100% 50%", "50% 100%", "0% 0%"] }}
          transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
        />
      )}

      <div className="relative grid md:grid-cols-[320px_1fr] gap-6 p-6 md:p-10">
        <div className="flex md:block items-center md:items-start gap-4">
          <img
            src={founderImg}
            alt="CEO"
            className="w-28 h-28 md:w-full md:h-72 object-cover rounded-2xl border shadow-sm"
            loading="lazy"
          />
          <div className="md:mt-4">
            <div className="text-xs uppercase tracking-wider text-blue-700/90 font-semibold">
              CEO’s Message
            </div>
            <h3 className="text-xl md:text-2xl font-extrabold text-blue-900">
              A promise of clarity, care & excellence
            </h3>
          </div>
        </div>

        <div className="leading-relaxed text-gray-800">
          <p>
            Since 2017, I have had the privilege of working in the Education and Migration industry —
            beginning as an Education Counsellor and later as a Migration Agent Assistant. These
            experiences have given me not only a deep understanding of academic and visa processes,
            but also the immense satisfaction of helping students and clients turn their dreams of
            studying, living, and thriving in Australia into reality.
          </p>
          <p className="mt-3">
            With this knowledge and passion, I founded{" "}
            <span className="font-semibold">Glide Education &amp; Visa Services</span> — a company built
            on trust, transparency, and a commitment to excellence. Our mission is simple: to provide
            every client with personalised guidance, tailored solutions, and unwavering support at every
            step of their journey.
          </p>
          <p className="mt-3">
            Starting Glide Education &amp; Visa Services is more than just a professional milestone — it’s
            a promise to continue empowering students and migrants to achieve their ambitions with
            confidence. Together, we’ll make your journey to Australia smooth, informed, and successful.
          </p>

          <div className="mt-6">
            <div className="h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent" />
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="font-semibold text-blue-900">
                — CEO, Glide Education &amp; Visa Services
              </span>
              <span className="text-xs text-gray-500">Adelaide, South Australia</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

/* =========================
   Particles config (hero)
========================= */
const useParticlesOptions = () =>
  useMemo(
    () => ({
      fullScreen: { enable: false },
      background: { color: { value: "transparent" } },
      fpsLimit: 60,
      particles: {
        number: { value: 40, density: { enable: true, area: 800 } },
        color: { value: ["#60a5fa", "#34d399", "#a78bfa"] },
        links: { enable: true, color: "#ffffff", opacity: 0.2, distance: 150 },
        move: { enable: true, speed: 1.2, outModes: { default: "out" } },
        opacity: { value: 0.6 },
        size: { value: { min: 1, max: 3 } },
      },
      interactivity: {
        events: { onHover: { enable: true, mode: "repulse" }, resize: true },
        modes: { repulse: { distance: 100, duration: 0.4 } },
      },
      detectRetina: true,
    }),
    []
  );

/* =========================
   PAGE
========================= */
export default function About() {
  const heroFade = useFadeUp(0);
  const particlesOptions = useParticlesOptions();


  // Init AOS once
  // ✅ Clean
useEffect(() => {
  AOS.init({ duration: 600, offset: 80, once: true, easing: "ease-out" });
}, []);


  // tsParticles init (slim)
  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  return (
    <>
      <Seo
        title="About Us | Glide Education & Visa Services"
        description="We assist students choose the right course, secure admissions, and navigate Australian visas with transparent, student-first guidance."
      />

      {/* Global animated background */}
      <BackgroundFX />

      {/* Hero with particles */}
      <motion.section
        {...heroFade}
        className="relative min-h-[60vh] grid place-items-center overflow-hidden"
      >
        <img
          src={aboutBg}
          alt="Students in Australia"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/60" />

        {/* Particles layer */}
        <div className="absolute inset-0">
          <Particles id="tsparticles" init={particlesInit} options={particlesOptions} />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 text-center text-white">
          <img src={logo} alt="Glide Logo" className="mx-auto w-20 h-20 rounded-xl mb-4" />
          <h1 className="text-3xl md:text-5xl font-extrabold">
            Student-first guidance for study & visas in Australia
          </h1>
          <p className="mt-4 text-white/90 max-w-3xl mx-auto">
            We simplify choices, paperwork, and policy changes — so you can focus on your goals. From
            shortlisting courses to visa lodgement and arrival, we’re with you at every step.
          </p>
          <div className="mt-6">
            <Link
              to="/contact"
              className="inline-block bg-white text-blue-700 hover:bg-blue-50 px-6 py-2 rounded-md font-semibold"
            >
              Book a Free Session
            </Link>
          </div>
        </div>

        {/* faint animated glow */}
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(30% 30% at 50% 50%, rgba(255,255,255,0.16), transparent 60%)",
            backgroundSize: "200% 200%",
          }}
          initial={{ backgroundPosition: "50% 100%", opacity: 0.7 }}
          animate={{ backgroundPosition: ["50% 100%", "0% 0%", "100% 0%", "50% 100%"] }}
          transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
        />
      </motion.section>

      <div className="relative max-w-6xl mx-auto px-6 py-12">
        {/* floating accent underline */}
        <div
          data-aos="fade-zoom-in"
          className="absolute -top-6 left-6 right-6 h-16 bg-gradient-to-r from-blue-200/40 via-emerald-200/40 to-violet-200/40 blur-2xl rounded-full -z-10"
        />

        {/* Story */}
        <Section
          id="story"
          title="Our Story"
          intro="Glide began with a simple idea: international students deserve clear, honest guidance and a calm, step-by-step path to success."
        >
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <Card className="overflow-hidden" tilt>
              <img
                src={heroImg}
                alt="Guiding students"
                className="w-full h-80 object-cover"
                loading="lazy"
              />
            </Card>
            <div className="leading-relaxed">
              <p>
                We’ve sat with students at every stage — planning applications, refining statements,
                preparing funds evidence, booking health checks, and reading visa conditions line by line.
                That lived understanding shapes how we support you today.
              </p>
              <p className="mt-2">
                At Glide, you’ll get timelines you can trust, documents checked with care, and advice
                that puts your outcomes first — not ours.
              </p>
            </div>
          </div>
        </Section>

        {/* CEO Message */}
        <Section
          id="ceo-message"
          title="CEO’s Message"
          intro="A promise of clarity, integrity and care — from our leadership to every student we support."
        >
          <CEOSpotlight />
        </Section>

        {/* Stats */}
        <Section id="impact" title="Our Impact">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat i={0} k="700+" v="Students Helped" sub="Across courses & visas" />
            <Stat i={1} k="1:1" v="Personal Guidance" sub="Tailored to your goals" />
            <Stat i={2} k="Multi-" v="University Options" sub="Compare across providers" />
            <Stat i={3} k="End-to-End" v="Course → Visa → Arrival" sub="Streamlined support" />
          </div>
        </Section>

        {/* Values */}
        <Section id="values" title="Our Values" intro="What you can expect from us, every time.">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ["🎯", "Student-first", "Your outcomes drive our decisions."],
              ["🔍", "Transparency", "No surprises. Clear timelines & fees."],
              ["🧭", "Integrity", "Evidence over hype; honest guidance."],
              ["🛡️", "Quality", "Meticulous checks and complete files."],
              ["🤝", "Care", "Real support beyond visa decisions."],
              ["⚡", "Agility", "We adapt quickly to policy changes."],
            ].map(([e, t, d], i) => (
              <ValueCard key={t} emoji={e} title={t} text={d} i={i} />
            ))}
          </div>
        </Section>

        {/* Trust markers */}
        <Section
          id="trust"
          title="Trust & Professional Standards"
          intro="We align to industry best practice and collaborate with registered professionals where required."
        >
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-6" tilt>
              <div className="flex items-center gap-3">
                <img src={logo} alt="Qualified badge" className="w-10 h-10 rounded" />
                <div className="font-semibold text-blue-800">Qualified Counselling</div>
              </div>
              <p className="text-gray-700 mt-2">
                Guidance shaped by formal training and hands-on admissions experience, with rigorous
                document review and clear timelines.
              </p>
            </Card>

            {/* CHANGED: Migration Team Assist (replaces Migration Oversight) */}
            <Card className="p-6" tilt>
              <div className="flex items-center gap-3">
                <img src={logo} alt="Migration team assist" className="w-10 h-10 rounded" />
                <div className="font-semibold text-blue-800">Migration Team Assist</div>
              </div>
              <p className="text-gray-700 mt-2">
                Our migration team assists with visa strategy and document preparation. When formal
                migration advice or representation is required, we partner with registered professionals
                to ensure compliance and accuracy.
              </p>
            </Card>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            *Exact accreditations/registrations depend on service and location. We keep guidance aligned
            with current policy and official sources.
          </p>
        </Section>

        {/* Journey */}
        <Section
          id="journey"
          title="Our Journey"
          intro="Milestones that shaped how we support students today."
        >
          <div className="border-l-2 border-blue-200 pl-4">
            <TimelineItem
              i={0}
              year="2017"
              title="Groundwork & experience"
              text="From counselling to migration support — building deep, hands-on expertise."
              img={img1}
            />
            <TimelineItem
              i={1}
              year="2024"
              title="From idea to impact"
              text="Glide was founded to remove confusion and bring calm, accurate guidance to students."
              img={idea}
            />
            <TimelineItem
              i={2}
              year="2025"
              title="End-to-end service"
              text="We streamlined the path from course shortlisting to visa lodgement and arrival."
              img={workshop}
            />
          </div>
        </Section>

        {/* Testimonials */}
        <Section id="voices" title="Voices">
          <Testimonials />
        </Section>

        {/* CTA */}
        <motion.div {...useFadeUp(0.04)}>
          <Card className="mt-8 p-6" tilt={false}>
            <h3 className="text-xl font-bold text-blue-900">
              Ready to plan your study & visa pathway?
            </h3>
            <p className="text-gray-700 mt-1">
              Get a free, no-obligation session to map your options, timeline and documents.
            </p>
            <div className="mt-4">
              <Link
                to="/contact"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md"
              >
                Talk to a consultant
              </Link>
            </div>
          </Card>
        </motion.div>
      </div>
    </>
  );
}
